import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SHARED_ENVIRONMENT } from '@k12/environment';

/**
 * The caller's Entra authorization surface, with the two kinds of membership kept apart.
 *
 * Only `appRoles` grants anything. `groups` is largely positional — provisioning puts every new
 * registrant in their portal's generic group, and seeded users additionally carry the default
 * automation groups — so a guard that treats a group name as a role sees those as unrecognised roles
 * and locks the user out.
 */
export interface EntraUserAccess {
  appRoles: string[];
  groups: string[];
}

@Injectable({ providedIn: 'root' })
export class EntraService {
  private readonly http = inject(HttpClient);
  private readonly sharedEnv = inject(SHARED_ENVIRONMENT);

  async getEntraUserGroups(): Promise<string[]> {
    try {
      const url = `${this.sharedEnv.adminApiUrl}/entraUserAccess/groups`; // include /api if needed
      return await firstValueFrom(this.http.get<string[]>(url));
    } catch (e) {
      console.error('getEntraUserAccess failed', e);
      return [];
    }
  }

  /**
   * Returns `null` rather than an empty result when the probe fails, so callers can tell "this user
   * holds nothing" from "we could not find out". The two demand opposite handling: the first is a
   * brand-new registrant, the second must never be used to route or block anyone.
   */
  async getEntraUserAccess(): Promise<EntraUserAccess | null> {
    try {
      const url = `${this.sharedEnv.adminApiUrl}/entraUserAccess/access`;
      const access = await firstValueFrom(this.http.get<EntraUserAccess>(url));
      return {
        appRoles: access?.appRoles ?? [],
        groups: access?.groups ?? []
      };
    } catch (e) {
      console.error('getEntraUserAccess failed', e);
      return null;
    }
  }
}
